import { defineStore } from 'pinia'
import type { Surface, SurfacePagedResult } from '~/types/api'
import { APIFilters } from '~/utils/APIFilters'
import type { RunningPromises } from '~/types/store'
import type { Ref } from 'vue'

type SurfacesCollectionCache = {
  [key: number]: Surface[]
}

export const useSurfacesCollectionsStore = defineStore(
  'surfacesCollections',
  () => {
    const surfacesRepository = createSurfacesRepository()

    const { apiAlert } = useAlert()

    const fetchedSurfacesCollections = ref<SurfacesCollectionCache>(
      {} as SurfacesCollectionCache
    )

    const runningPromises = ref<RunningPromises<Surface[]>>(
      {} as RunningPromises<Surface[]>
    )

    function loadSurfaceCollection(id: number) {
      if (runningPromises.value[id] !== undefined) {
        return runningPromises.value[id]
      }

      const filter = [
        {
          [APIFilterOP.EQUALS]: {
            'surfaceCollections.nid': id,
          },
        },
      ]
      const fetchPromise = surfacesRepository
        .getAllSurfaces({
          filter: APIFilters.makeFilter(filter),
          sort: APIFilters.makeSort({ nid: 'ASC' }),
        })
        .then((res) => {
          fetchedSurfacesCollections.value[id] = res.items ?? []
        })
        .catch(apiAlert)
      runningPromises.value[id] = fetchPromise
      return fetchPromise
    }

    async function getSurfaceCollections(id: number) {
      if (fetchedSurfacesCollections.value[id] === undefined) {
        await loadSurfaceCollection(id)
      }
      return fetchedSurfacesCollections.value[id]
    }

    return {
      fetchedSurfacesCollections,
      getSurfaceCollections,
    }
  }
)
